/**
 * ModalSheet — iOS-style fade+zoom modal wrapper, vanilla TS port of
 * the React helper in _design_ref_mobile_2026-05-24/render-pair.html.
 *
 * Behavior:
 *  - Mounted inside a host container (usually the mobile config root).
 *  - On open(): inserted, then on the next animation frame the visible
 *    class flips so transform/opacity transition runs.
 *  - On close(): visible class is removed; node is detached after the
 *    transition completes (300ms safety timer).
 *  - Click anywhere not inside a <button> dismisses.
 *  - Scrolls its host to the top on open so the sheet animates from a
 *    consistent origin (matches the mockup phoneRef.scrollTop = 0).
 */

export interface ModalSheetOptions {
  host: HTMLElement;
  background: string;
  onDismiss: () => void;
}

export interface ModalSheet {
  readonly root: HTMLElement;
  readonly body: HTMLElement;
  open(): void;
  close(): void;
  setTitle(title: string, countLabel?: string): void;
  clearBody(): void;
}

const ENTER_MS = 300;

export function createModalSheet(opts: ModalSheetOptions): ModalSheet {
  const root = document.createElement("div");
  root.className = "mob-sheet"; // full-screen dim backdrop
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");

  // The visible card: sized to its content (not full-screen) and carrying the host
  // background, so the sheet "just fits" the options instead of taking over the page.
  const panel = document.createElement("div");
  panel.className = "mob-sheet__panel";
  panel.style.background = opts.background;

  const head = document.createElement("div");
  head.className = "mob-sheet__head";

  const titleEl = document.createElement("span");
  titleEl.className = "mob-sheet__title";
  head.appendChild(titleEl);

  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "mob-sheet__close";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.appendChild(createCloseXIcon(12, 1.8));
  closeBtn.addEventListener("click", () => api.close());
  head.appendChild(closeBtn);

  const body = document.createElement("div");
  body.className = "mob-sheet__body";

  panel.append(head, body);
  root.appendChild(panel);

  // Click on the backdrop (outside the card) → dismiss.
  root.addEventListener("click", (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest(".mob-sheet__panel")) return;
    api.close();
  });

  let closeTimer: number | null = null;
  let mounted = false;

  const api: ModalSheet = {
    root,
    body,
    open() {
      if (closeTimer != null) {
        clearTimeout(closeTimer);
        closeTimer = null;
      }
      if (!mounted) {
        opts.host.appendChild(root);
        mounted = true;
      }
      // Reset scroll on host so the sheet starts from top.
      opts.host.scrollTop = 0;
      // Double-rAF so the browser commits the initial transform/opacity
      // before we flip to .is-visible (otherwise no transition fires).
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root.classList.add("is-visible");
        });
      });
    },
    close() {
      root.classList.remove("is-visible");
      if (closeTimer != null) clearTimeout(closeTimer);
      closeTimer = window.setTimeout(() => {
        if (mounted) {
          root.remove();
          mounted = false;
        }
        closeTimer = null;
        opts.onDismiss();
      }, ENTER_MS);
    },
    setTitle(title: string, countLabel?: string) {
      titleEl.textContent = title;
      if (countLabel) {
        const sep = document.createElement("span");
        sep.className = "mob-sheet__count";
        sep.textContent = ` · ${countLabel}`;
        titleEl.appendChild(sep);
      }
    },
    clearBody() {
      body.innerHTML = "";
    },
  };

  return api;
}

// Re-exported locally so ModalSheet doesn't have to import a sibling module
// before the bundler has resolved the dependency graph.
function createCloseXIcon(size: number, strokeWidth: number): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", String(strokeWidth));
  svg.setAttribute("stroke-linecap", "round");
  const pad = size === 12 ? 2 : Math.round(size * 0.18);
  const end = size - pad;
  const l1 = document.createElementNS(ns, "line");
  l1.setAttribute("x1", String(pad));
  l1.setAttribute("y1", String(pad));
  l1.setAttribute("x2", String(end));
  l1.setAttribute("y2", String(end));
  const l2 = document.createElementNS(ns, "line");
  l2.setAttribute("x1", String(end));
  l2.setAttribute("y1", String(pad));
  l2.setAttribute("x2", String(pad));
  l2.setAttribute("y2", String(end));
  svg.append(l1, l2);
  return svg;
}
